
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FaqFormData } from "@/types/faq-types";
import { useToast } from "@/components/ui/use-toast";

export const useFaqActions = (loadFaqsCallback: () => Promise<void>) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const createFaq = async (faqData: FaqFormData) => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase
        .from('faqs')
        .insert({
          question: faqData.question,
          answer: faqData.answer,
          image_url: faqData.image_url || null,
          video_url: faqData.video_url || null,
          status: faqData.status,
          display_order: faqData.display_order,
          active: faqData.active
        })
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      await loadFaqsCallback();
      
      toast({
        title: "Success",
        description: "FAQ created successfully",
      });
      
      return data?.id || null;
    } catch (error: any) {
      console.error("Error creating FAQ:", error);
      toast({
        variant: "destructive",
        title: "Error creating FAQ",
        description: error.message || "An unknown error occurred"
      });
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const updateFaq = async (id: string, faqData: FaqFormData) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('faqs')
        .update({
          question: faqData.question,
          answer: faqData.answer,
          image_url: faqData.image_url || null,
          video_url: faqData.video_url || null,
          status: faqData.status,
          display_order: faqData.display_order,
          active: faqData.active
        })
        .eq('id', id);

      if (error) {
        throw error;
      }

      await loadFaqsCallback();
      
      toast({
        title: "Success",
        description: "FAQ updated successfully",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error updating FAQ:", error);
      toast({
        variant: "destructive",
        title: "Error updating FAQ",
        description: error.message || "An unknown error occurred"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteFaq = async (id: string) => {
    setIsProcessing(true);
    try {
      console.log("Deleting FAQ with ID:", id);
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      console.log("FAQ deleted successfully, reloading data");
      
      // Ensure data is reloaded after successful deletion
      await loadFaqsCallback();
      
      toast({
        title: "Success",
        description: "FAQ deleted successfully",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error deleting FAQ:", error);
      toast({
        variant: "destructive",
        title: "Error deleting FAQ",
        description: error.message || "An unknown error occurred"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    createFaq,
    updateFaq,
    deleteFaq,
    isProcessing
  };
};
